/**
 * Spatial Pinning Editor — Admin tool for mapping rooms to floor plan coordinates
 *
 * DRR-179 Phase 2: Click on a floor plan image to place rooms/zones.
 * Each click creates a LOCATED_AT scope edge with {x, y, floor} metadata.
 *
 * Two-phase layout:
 * - Phase 1 (no floor plan): Full-width PDF page picker + uploader
 * - Phase 2 (floor plan loaded): Left = floor plan with pins, Right = room list
 *
 * Floor tabs allow switching between assigned floors.
 * Coordinates are normalized (0.0–1.0) relative to image dimensions.
 * Admin can drag to reposition, click existing pin to remove.
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import {
  Stack,
  Group,
  Paper,
  Text,
  Button,
  Badge,
  ScrollArea,
  Box,
  NavLink,
  Image,
  Alert,
  ActionIcon,
  Tooltip,
  FileButton,
  Tabs,
  Container,
  Slider,
  TextInput,
} from '@mantine/core';
import {
  IconMapPin,
  IconDeviceFloppy,
  IconTrash,
  IconArrowBackUp,
  IconCheck,
  IconWand,
  IconX,
  IconUpload,
  IconBuilding,
  IconZoomIn,
  IconZoomOut,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  useGetActorsByTypeQuery,
  useAddScopeEdgeMutation,
  useRemoveScopeEdgeMutation,
  useCompleteTasksByToolHintMutation,
  GqlActorType,
} from '@/generated/graphql';
import { InlineDocumentUploader, type InlineUploadResult } from '@ferp/workflow-forms';
import { PdfFloorPlanPicker, type FloorAssignment } from '@/components/pdf';
import { useTenantId, useViewLayer } from '@/auth';
import { useFloorPlanUrl } from '@/hooks/useFloorPlanUrl';
import { useFloorPlanAutoExtract, type RoomSuggestion } from '@/hooks/useFloorPlanAutoExtract';
import { useSpatialLayout } from '@/hooks/useSpatialLayout';

interface PinnedRoom {
  actorId: string;
  name: string;
  x: number; // 0.0–1.0
  y: number; // 0.0–1.0
  floor: number;
}

interface FloorImage {
  floor: number;
  label: string;
  blobUrl: string;
  pageNumber?: number; // PDF page this was extracted from (for assignedPages tracking)
}

export function SpatialPinningEditor() {
  const tenantId = useTenantId();
  const viewLayer = useViewLayer();
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // DRR-102: Only admin/architect can upload and configure spatial layout
  const canEdit = viewLayer === 'admin' || viewLayer === 'architect';

  // State
  const [floorPlanDocId, setFloorPlanDocId] = useState<string | null>(null);
  const [uploadedPdfFile, setUploadedPdfFile] = useState<File | null>(null);
  const [floorImages, setFloorImages] = useState<FloorImage[]>([]);
  const [activeFloor, setActiveFloor] = useState<string | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [pinnedRooms, setPinnedRooms] = useState<PinnedRoom[]>([]);
  const [zoom, setZoom] = useState(100);
  const [editingFloorLabel, setEditingFloorLabel] = useState<number | null>(null);
  const [editLabelValue, setEditLabelValue] = useState('');

  // DRR-179 P1: Resolve document ID → signed S3 URL (survives refresh)
  const { url: signedUrl } = useFloorPlanUrl(floorPlanDocId);

  // DRR-179 Phase 3: Auto-extraction via Claude vision
  const { suggest, loading: extracting, error: extractError } = useFloorPlanAutoExtract();
  const [pendingSuggestions, setPendingSuggestions] = useState<RoomSuggestion[]>([]);
  const [saving, setSaving] = useState(false);

  // DRR-179: Scope edge mutations for persisting spatial coordinates
  const [addScopeEdge] = useAddScopeEdgeMutation();
  const [removeScopeEdge] = useRemoveScopeEdgeMutation();

  // DRR-934: Auto-complete onboarding tasks when tool saves successfully
  const [completeTasksByToolHint] = useCompleteTasksByToolHintMutation();

  // DRR-179: Load persisted spatial layout (survives page refresh)
  const {
    layout: persistedLayout,
    floorPlanRefs,
    loading: layoutLoading,
  } = useSpatialLayout();

  // Initialize from persisted data on mount
  useEffect(() => {
    if (layoutLoading) return;

    // Load floor plan document ID from persisted refs
    const firstRef = floorPlanRefs[0];
    if (firstRef && !floorPlanDocId) {
      setFloorPlanDocId(firstRef.documentId);
      setActiveFloor(String(firstRef.floor));
    }

    // Load persisted pins
    if (persistedLayout.length > 0 && pinnedRooms.length === 0) {
      const loadedPins: PinnedRoom[] = persistedLayout
        .filter((node) => node.x !== null && node.y !== null)
        .map((node) => ({
          actorId: node.actorId,
          name: node.name,
          x: node.x!,
          y: node.y!,
          floor: node.floor,
        }));
      if (loadedPins.length > 0) {
        setPinnedRooms(loadedPins);
      }
    }
  }, [layoutLoading, floorPlanRefs, persistedLayout, floorPlanDocId, pinnedRooms.length]);

  // Load room actors (scope level 2 = individual rooms)
  const { data: actorData, loading: actorsLoading } = useGetActorsByTypeQuery({
    variables: { actorType: GqlActorType.SUBJECT },
    skip: !tenantId,
  });

  const roomActors = useMemo(() => {
    return (actorData?.getActorsByType ?? []).map((a) => ({
      id: a.id,
      name: (a.name as string) ?? 'Unnamed',
    }));
  }, [actorData]);

  // Current floor number
  const currentFloorNum = activeFloor ? parseInt(activeFloor, 10) : null;

  // Pins for current floor only
  const currentFloorPins = useMemo(
    () => (currentFloorNum != null ? pinnedRooms.filter((r) => r.floor === currentFloorNum) : []),
    [pinnedRooms, currentFloorNum],
  );

  const pinnedIds = useMemo(() => new Set(pinnedRooms.map((r) => r.actorId)), [pinnedRooms]);

  // Current floor plan image URL (blob or signed)
  const currentFloorImage = useMemo(() => {
    if (!currentFloorNum) return signedUrl ?? null;
    const fi = floorImages.find((f) => f.floor === currentFloorNum);
    return fi?.blobUrl ?? signedUrl ?? null;
  }, [currentFloorNum, floorImages, signedUrl]);

  // Handle floor plan upload via chaos pipeline (single image)
  const handleFloorPlanUploaded = useCallback((result: InlineUploadResult) => {
    if (result.fileType.startsWith('image/') && result.previewUrl) {
      setFloorImages([{ floor: 1, label: 'Floor Plan', blobUrl: result.previewUrl }]);
      setFloorPlanDocId(result.documentId);
      setUploadedPdfFile(null);
      setActiveFloor('1');
    }
    if (result.fileType === 'application/pdf') {
      setFloorPlanDocId(result.documentId);
    }
  }, []);

  // Handle PDF file selection (local file picker)
  const handlePdfSelected = useCallback((file: File | null) => {
    if (file) {
      setUploadedPdfFile(file);
      setFloorImages([]);
      setActiveFloor(null);
    }
  }, []);

  // Handle page assignment from PdfFloorPlanPicker
  const handlePageAssigned = useCallback((assignment: FloorAssignment) => {
    const url = URL.createObjectURL(assignment.pngBlob);
    setFloorImages((prev) => {
      // Remove any existing assignment for this page number (not floor number)
      const filtered = prev.filter((f) => f.pageNumber !== assignment.pageNumber);
      return [
        ...filtered,
        {
          floor: assignment.floor,
          label: assignment.label,
          blobUrl: url,
          pageNumber: assignment.pageNumber,
        },
      ].sort((a, b) => a.floor - b.floor);
    });
    // Auto-switch to newly assigned floor
    setActiveFloor(String(assignment.floor));
  }, []);

  // Handle unassigning a floor page (pageNumber = PDF page, not floor)
  const handlePageUnassigned = useCallback((pageNumber: number) => {
    setFloorImages((prev) => {
      const removing = prev.find((f) => f.pageNumber === pageNumber);
      if (removing) URL.revokeObjectURL(removing.blobUrl);
      const next = prev.filter((f) => f.pageNumber !== pageNumber);
      // If we removed the active floor, switch to the first remaining
      if (removing && activeFloor === String(removing.floor)) {
        setActiveFloor(next[0] ? String(next[0].floor) : null);
      }
      return next;
    });
    // Remove pins on that floor
    setPinnedRooms((prev) => prev.filter((r) => r.floor !== pageNumber));
  }, [activeFloor]);

  // Rename a floor label (double-click tab to edit)
  const startRenameFloor = useCallback((floor: number) => {
    const fi = floorImages.find((f) => f.floor === floor);
    if (!fi) return;
    setEditingFloorLabel(floor);
    setEditLabelValue(fi.label);
  }, [floorImages]);

  const commitRenameFloor = useCallback(() => {
    if (editingFloorLabel == null || !editLabelValue.trim()) {
      setEditingFloorLabel(null);
      return;
    }
    setFloorImages((prev) =>
      prev.map((fi) =>
        fi.floor === editingFloorLabel ? { ...fi, label: editLabelValue.trim() } : fi,
      ),
    );
    setEditingFloorLabel(null);
  }, [editingFloorLabel, editLabelValue]);

  // Handle click on floor plan to place selected room
  const handleFloorPlanClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!selectedRoomId || !imageContainerRef.current || currentFloorNum == null) return;

      const rect = imageContainerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

      const roomName = roomActors.find((r) => r.id === selectedRoomId)?.name ?? selectedRoomId;

      setPinnedRooms((prev) => {
        const filtered = prev.filter((r) => r.actorId !== selectedRoomId);
        return [...filtered, { actorId: selectedRoomId, name: roomName, x, y, floor: currentFloorNum }];
      });

      // Auto-advance to next unpinned room
      const unpinned = roomActors.filter(
        (r) => r.id !== selectedRoomId && !pinnedIds.has(r.id),
      );
      setSelectedRoomId(unpinned[0]?.id ?? null);
    },
    [selectedRoomId, roomActors, pinnedIds, currentFloorNum],
  );

  // Remove a pin
  const removePin = useCallback((actorId: string) => {
    setPinnedRooms((prev) => prev.filter((r) => r.actorId !== actorId));
  }, []);

  // DRR-179 Phase 3: AI room extraction
  const handleAutoExtract = useCallback(async () => {
    if (!floorPlanDocId) return;
    const results = await suggest(floorPlanDocId, 'assisted_living');
    setPendingSuggestions(results);
  }, [floorPlanDocId, suggest]);

  const acceptSuggestion = useCallback(
    (suggestion: RoomSuggestion) => {
      if (currentFloorNum == null) return;
      const matchedRoom = roomActors.find(
        (r) => r.name.toLowerCase() === suggestion.label.toLowerCase(),
      );
      setPinnedRooms((prev) => [
        ...prev.filter((r) => r.actorId !== (matchedRoom?.id ?? suggestion.label)),
        {
          actorId: matchedRoom?.id ?? suggestion.label,
          name: matchedRoom?.name ?? suggestion.label,
          x: suggestion.x,
          y: suggestion.y,
          floor: currentFloorNum,
        },
      ]);
      setPendingSuggestions((prev) => prev.filter((s) => s.label !== suggestion.label));
    },
    [roomActors, currentFloorNum],
  );

  const acceptAllSuggestions = useCallback(() => {
    for (const s of pendingSuggestions) {
      acceptSuggestion(s);
    }
  }, [pendingSuggestions, acceptSuggestion]);

  const dismissSuggestion = useCallback((label: string) => {
    setPendingSuggestions((prev) => prev.filter((s) => s.label !== label));
  }, []);

  // Save — persist LOCATED_AT scope edges with {x, y, floor, floor_plan_doc_id} metadata
  const handleSave = useCallback(async () => {
    if (!tenantId || pinnedRooms.length === 0) return;
    setSaving(true);

    // Use a facility-level actor as the source (the building itself).
    // For now, use the tenant ID as the source — in production this would be
    // the facility actor from constellation ring0.
    const facilityActorId = tenantId;

    // Build floor → document ID map from floorImages + floorPlanDocId
    const floorDocMap = new Map<number, string>();
    if (floorPlanDocId) {
      // Single image upload: all floors share the same document
      for (const fi of floorImages) {
        floorDocMap.set(fi.floor, floorPlanDocId);
      }
      // Fallback for floor 1 if no floorImages yet
      if (floorImages.length === 0) {
        floorDocMap.set(1, floorPlanDocId);
      }
    }

    let saved = 0;
    let failed = 0;

    for (const pin of pinnedRooms) {
      try {
        // Include floor_plan_doc_id in metadata so useSpatialLayout can retrieve it on refresh
        const metadata: Record<string, unknown> = {
          x: pin.x,
          y: pin.y,
          floor: pin.floor,
        };

        const docId = floorDocMap.get(pin.floor);
        if (docId) {
          metadata.floor_plan_doc_id = docId;
        }

        // Include floor label if available
        const floorInfo = floorImages.find((fi) => fi.floor === pin.floor);
        if (floorInfo?.label) {
          metadata.floor_label = floorInfo.label;
        }

        await addScopeEdge({
          variables: {
            input: {
              // Edge direction: Room (source) is LOCATED_AT Facility (target)
              // useSpatialLayout expects sourceName = room actor
              sourceActorId: pin.actorId,
              targetActorId: facilityActorId,
              edgeType: 'LOCATED_AT',
              metadata,
            } as never, // TODO: regenerate graphql.ts after gateway restart — metadata field added to AddScopeEdgeInput
          },
        });
        saved++;
      } catch (err) {
        console.error(`Failed to save pin for ${pin.name}:`, err);
        failed++;
      }
    }

    setSaving(false);

    if (failed === 0) {
      notifications.show({
        title: 'Spatial layout saved',
        message: `${saved} rooms mapped across ${floorImages.length} floor(s)`,
        color: 'green',
      });

      // DRR-934: Auto-complete onboarding tasks that use this tool
      try {
        const result = await completeTasksByToolHint({
          variables: { toolHint: 'spatial_pinning_editor' },
        });
        const completedCount = result.data?.completeTasksByToolHint ?? 0;
        if (completedCount > 0) {
          notifications.show({
            title: 'Onboarding tasks completed',
            message: `${completedCount} task(s) auto-completed`,
            color: 'teal',
          });
        }
      } catch (err) {
        // Non-fatal — the spatial layout is already saved
        console.warn('DRR-934: Failed to auto-complete tasks:', err);
      }
    } else {
      notifications.show({
        title: 'Partial save',
        message: `${saved} saved, ${failed} failed — check console for details`,
        color: 'orange',
      });
    }
  }, [tenantId, pinnedRooms, floorImages, addScopeEdge, completeTasksByToolHint]);

  // ── Phase 1: No floor images yet → show uploader + PDF picker full-width ──
  const hasFloorPlan = floorImages.length > 0;

  if (!hasFloorPlan) {
    return (
      <Container size="lg" py="md">
        <Stack gap="lg">
          <Text fw={700} size="lg">Spatial Layout Editor</Text>

          {!canEdit && (
            <Alert color="yellow" variant="light">
              Only administrators and architects can upload and configure facility floor plans.
              Contact your admin to set up the spatial layout.
            </Alert>
          )}

          <Paper withBorder radius="md" p="xl">
            <Stack gap="md" align="center">
              <IconBuilding size={48} color="var(--mantine-color-dimmed)" />
              <Text size="lg" fw={600}>
                {canEdit ? 'Upload a floor plan to get started' : 'No floor plan configured'}
              </Text>
              <Text size="sm" c="dimmed" maw={500} ta="center">
                {canEdit
                  ? "Upload an image (PNG/JPEG) or a multi-page PDF of your facility\u2019s architectural plans. For PDFs, you\u2019ll select which pages are floor plans."
                  : 'An administrator or architect needs to upload the facility floor plan before rooms can be mapped.'}
              </Text>

              {canEdit && (
                <Group gap="md" mt="md">
                  <InlineDocumentUploader
                    label="Upload Floor Plan"
                    accept="image/png,image/jpeg,application/pdf"
                    sourceSystem="spatial-pinning-editor"
                    metadata={{ document_type: 'facility_floor_plan' }}
                    onUploadComplete={handleFloorPlanUploaded}
                    showPreview={false}
                    maxHeight={80}
                  />
                  <Text size="sm" c="dimmed">or</Text>
                  <FileButton onChange={handlePdfSelected} accept="application/pdf">
                    {(props) => (
                      <Button {...props} variant="light" leftSection={<IconUpload size={16} />}>
                        Select PDF from device
                      </Button>
                    )}
                  </FileButton>
                </Group>
              )}
            </Stack>
          </Paper>

          {/* Full-width PDF page picker */}
          {uploadedPdfFile && canEdit && (
            <Paper withBorder radius="md" p="md">
              <PdfFloorPlanPicker
                pdfSource={uploadedPdfFile}
                onPageAssigned={handlePageAssigned}
                onPageUnassigned={handlePageUnassigned}
                assignedPages={new Map(
                  floorImages
                    .filter((fi) => fi.pageNumber != null)
                    .map((fi) => [
                      fi.pageNumber!,
                      { floor: fi.floor, label: fi.label },
                    ]),
                )}
                thumbnailWidth={280}
              />
            </Paper>
          )}
        </Stack>
      </Container>
    );
  }

  // ── Phase 2: Floor plan loaded → pinning mode ──
  return (
    <Stack gap="md" style={{ height: 'calc(100vh - 120px)' }}>
      {/* Header toolbar */}
      <Group justify="space-between">
        <Group gap="sm">
          <Text fw={700} size="lg">Spatial Layout Editor</Text>
          {floorImages.length > 0 && (
            <Badge variant="light" color="blue">{floorImages.length} floor(s)</Badge>
          )}
        </Group>
        {canEdit && (
          <Group gap="xs">
            <Button
              variant="light"
              color="violet"
              size="sm"
              leftSection={<IconWand size={14} />}
              onClick={handleAutoExtract}
              loading={extracting}
              disabled={!floorPlanDocId}
            >
              AI Suggest
            </Button>
            <Button
              variant="light"
              color="red"
              size="sm"
              leftSection={<IconArrowBackUp size={14} />}
              onClick={() => setPinnedRooms([])}
              disabled={pinnedRooms.length === 0}
            >
              Reset
            </Button>
            <Button
              variant="filled"
              size="sm"
              leftSection={<IconDeviceFloppy size={14} />}
              onClick={handleSave}
              loading={saving}
              disabled={pinnedRooms.length === 0}
            >
              Save ({pinnedRooms.length} pins)
            </Button>
            {uploadedPdfFile && (
              <FileButton onChange={handlePdfSelected} accept="application/pdf">
                {(props) => (
                  <Button {...props} variant="subtle" size="sm" leftSection={<IconUpload size={14} />}>
                    Add Floor
                  </Button>
                )}
              </FileButton>
            )}
          </Group>
        )}
      </Group>

      {/* Floor tabs + zoom control */}
      <Group justify="space-between" align="flex-end">
        <Tabs value={activeFloor} onChange={setActiveFloor}>
          <Tabs.List>
            {floorImages.map((fi) => (
              <Tabs.Tab
                key={fi.floor}
                value={String(fi.floor)}
                leftSection={<IconBuilding size={14} />}
                rightSection={
                  canEdit ? (
                    <Tooltip label={`Remove ${fi.label}`}>
                      <span
                        role="button"
                        tabIndex={0}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          cursor: 'pointer',
                          color: 'var(--mantine-color-red-6)',
                          opacity: 0.6,
                        }}
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); handlePageUnassigned(fi.floor); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handlePageUnassigned(fi.floor); } }}
                      >
                        <IconX size={10} />
                      </span>
                    </Tooltip>
                  ) : undefined
                }
              >
                {editingFloorLabel === fi.floor ? (
                  <TextInput
                    size="xs"
                    value={editLabelValue}
                    onChange={(e) => setEditLabelValue(e.currentTarget.value)}
                    onBlur={commitRenameFloor}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRenameFloor();
                      if (e.key === 'Escape') setEditingFloorLabel(null);
                    }}
                    autoFocus
                    style={{ width: 120 }}
                    styles={{ input: { height: 24, minHeight: 24, fontSize: 12 } }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span onDoubleClick={(e) => { e.stopPropagation(); if (canEdit) startRenameFloor(fi.floor); }}>
                    {fi.label}
                  </span>
                )}
                <Badge size="xs" variant="light" color="blue" ml={6}>
                  {pinnedRooms.filter((r) => r.floor === fi.floor).length}
                </Badge>
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>

        <Group gap="xs" align="center">
          <ActionIcon variant="subtle" size="sm" onClick={() => setZoom((z) => Math.max(50, z - 25))}>
            <IconZoomOut size={14} />
          </ActionIcon>
          <Slider
            value={zoom}
            onChange={setZoom}
            min={50}
            max={300}
            step={25}
            style={{ width: 100 }}
            size="xs"
            label={(v) => `${v}%`}
          />
          <ActionIcon variant="subtle" size="sm" onClick={() => setZoom((z) => Math.min(300, z + 25))}>
            <IconZoomIn size={14} />
          </ActionIcon>
          <Text size="xs" c="dimmed" w={40} ta="center">{zoom}%</Text>
        </Group>
      </Group>

      {/* Main content: floor plan + room sidebar */}
      <Group align="flex-start" gap="md" wrap="nowrap" style={{ flex: 1, minHeight: 0 }}>
        {/* Floor plan with pins — scrollable at zoom > 100% */}
        <ScrollArea style={{ flex: 1 }} type="auto" offsetScrollbars>
          <Paper
            ref={imageContainerRef}
            withBorder
            radius="md"
            style={{
              position: 'relative',
              width: `${zoom}%`,
              aspectRatio: '4 / 3',
              cursor: canEdit && selectedRoomId ? 'crosshair' : 'default',
              overflow: 'hidden',
              background: 'var(--mantine-color-gray-1)',
            }}
            onClick={canEdit ? handleFloorPlanClick : undefined}
          >
            {currentFloorImage ? (
              <Image
                src={currentFloorImage}
                alt="Floor plan"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Stack align="center" justify="center" style={{ position: 'absolute', inset: 0 }} gap="xs">
                <IconMapPin size={48} color="var(--mantine-color-dimmed)" />
                <Text c="dimmed" size="sm">Select a floor tab above</Text>
              </Stack>
            )}

            {/* Pinned room markers (current floor) */}
            {currentFloorPins.map((pin) => (
              <Tooltip key={pin.actorId} label={`${pin.name} — click to remove`}>
                <Box
                  style={{
                    position: 'absolute',
                    left: `${pin.x * 100}%`,
                    top: `${pin.y * 100}%`,
                    transform: 'translate(-50%, -100%)',
                    zIndex: 5,
                  }}
                >
                  <ActionIcon
                    variant="filled"
                    color="blue"
                    radius="xl"
                    size="md"
                    onClick={(e) => { e.stopPropagation(); removePin(pin.actorId); }}
                  >
                    <IconMapPin size={14} />
                  </ActionIcon>
                  <Text
                    size="xs"
                    fw={600}
                    ta="center"
                    style={{
                      background: 'var(--mantine-color-body)',
                      borderRadius: 'var(--mantine-radius-xs)',
                      padding: '1px 4px',
                      marginTop: 2,
                      whiteSpace: 'nowrap',
                      boxShadow: 'var(--mantine-shadow-xs)',
                    }}
                  >
                    {pin.name}
                  </Text>
                </Box>
              </Tooltip>
            ))}

            {/* AI suggestion markers */}
            {pendingSuggestions.map((s) => (
              <Tooltip key={`ai-${s.label}`} label={`AI: ${s.label} (${Math.round(s.confidence * 100)}%)`}>
                <Box
                  style={{
                    position: 'absolute',
                    left: `${s.x * 100}%`,
                    top: `${s.y * 100}%`,
                    transform: 'translate(-50%, -100%)',
                    zIndex: 3,
                  }}
                >
                  <ActionIcon
                    variant="outline"
                    color="violet"
                    radius="xl"
                    size="md"
                    style={{ borderStyle: 'dashed' }}
                    onClick={(e) => { e.stopPropagation(); acceptSuggestion(s); }}
                  >
                    <IconWand size={14} />
                  </ActionIcon>
                  <Text size="xs" c="violet" ta="center" style={{ whiteSpace: 'nowrap', opacity: 0.85 }}>
                    {s.label}
                  </Text>
                </Box>
              </Tooltip>
            ))}

            {/* Selection hint */}
            {selectedRoomId && (
              <Badge style={{ position: 'absolute', top: 8, left: 8 }} variant="filled" color="blue" size="lg">
                Click to place: {roomActors.find((r) => r.id === selectedRoomId)?.name}
              </Badge>
            )}
          </Paper>
        </ScrollArea>

        {/* Right sidebar: Room list */}
        <Paper withBorder radius="md" p="sm" style={{ width: 260, flexShrink: 0, alignSelf: 'stretch' }}>
          <Text fw={600} size="sm" mb="xs">Rooms / Zones</Text>
          <Text size="xs" c="dimmed" mb="sm">
            Select a room, then click the floor plan to place it.
          </Text>
          <ScrollArea style={{ flex: 1, maxHeight: 'calc(100vh - 340px)' }}>
            <Stack gap={2}>
              {actorsLoading ? (
                <Text size="xs" c="dimmed">Loading...</Text>
              ) : roomActors.length === 0 ? (
                <Text size="xs" c="dimmed">No rooms found</Text>
              ) : (
                roomActors.map((room) => {
                  const isPinned = pinnedIds.has(room.id);
                  const isSelected = selectedRoomId === room.id;
                  return (
                    <NavLink
                      key={room.id}
                      label={room.name}
                      active={isSelected}
                      variant="filled"
                      leftSection={
                        isPinned ? (
                          <IconCheck size={14} color="var(--mantine-color-green-6)" />
                        ) : (
                          <IconMapPin size={14} />
                        )
                      }
                      rightSection={
                        isPinned ? (
                          <ActionIcon
                            size="xs"
                            variant="subtle"
                            color="red"
                            onClick={(e) => { e.stopPropagation(); removePin(room.id); }}
                          >
                            <IconTrash size={12} />
                          </ActionIcon>
                        ) : undefined
                      }
                      onClick={() => setSelectedRoomId(isSelected ? null : room.id)}
                      style={{ opacity: isPinned && !isSelected ? 0.6 : 1 }}
                    />
                  );
                })
              )}
            </Stack>
          </ScrollArea>
          <Text size="xs" c="dimmed" mt="xs">
            {pinnedRooms.length} / {roomActors.length} placed
          </Text>

          {/* AI Suggestions */}
          {pendingSuggestions.length > 0 && (
            <>
              <Group justify="space-between" mt="md" mb="xs">
                <Text fw={600} size="sm">AI Suggestions</Text>
                <Button variant="subtle" size="xs" color="green" onClick={acceptAllSuggestions}>
                  Accept All ({pendingSuggestions.length})
                </Button>
              </Group>
              <ScrollArea style={{ maxHeight: 200 }}>
                <Stack gap={2}>
                  {pendingSuggestions.map((s) => (
                    <Group key={s.label} gap={4} wrap="nowrap">
                      <Text size="xs" style={{ flex: 1 }} lineClamp={1}>{s.label}</Text>
                      <Badge size="xs" variant="light" color="violet">
                        {Math.round(s.confidence * 100)}%
                      </Badge>
                      <ActionIcon size="xs" variant="subtle" color="green" onClick={() => acceptSuggestion(s)}>
                        <IconCheck size={12} />
                      </ActionIcon>
                      <ActionIcon size="xs" variant="subtle" color="red" onClick={() => dismissSuggestion(s.label)}>
                        <IconX size={12} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              </ScrollArea>
            </>
          )}

          {extractError && (
            <Alert color="red" variant="light" mt="xs">
              <Text size="xs">{extractError}</Text>
            </Alert>
          )}
        </Paper>
      </Group>
    </Stack>
  );
}
